import { useFundingEventsFilter } from '@src/components/Filters/FilterBuilder/useFundingEventsFilter';
import {
  BlotterDensity,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type ColumnDef,
  type DateRangeFilter,
  type FundingEvent,
  type WebsocketRequest,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { useMemo } from 'react';

const COLUMNS: Column[] = [
  { type: 'account', field: 'MarketAccount', initialWidth: 200 },
  { type: 'currency', field: 'Asset' },
  { type: 'asset', field: 'PositionAsset' },
  { type: 'number', field: 'Amount', params: { currencyField: 'Asset' } },
  { type: 'number', field: 'Rate' },
  { type: 'date', field: 'TransactTime' },
  { type: 'text', field: 'SettleStatus' },
  { type: 'text', field: 'TransactionID', initialWidth: 250, initialHide: true },
] satisfies ColumnDef<FundingEvent>[];

export const useFundingEventsBlotter = () => {
  const persistedBlotterTable = usePersistedBlotterTable('FundingEvents');

  const filteredFundingEvents = useFundingEventsFilter({ persistedBlotterTable });
  const { blotterTableFilterProps, filterBuilderProps, clientSideFilter: clientLocalFilter } = filteredFundingEvents;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const request = useMemo<WebsocketRequest>(
    () => ({
      name: 'FundingEvent',
      tag: 'BLOTTER_FUNDING_EVENTS',
      ...removeEmptyFilters(onlyServerFilterKeys(filteredFundingEvents.filter)),
    }),
    [filteredFundingEvents.filter]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, FundingEvent>({
    request,
    columns: COLUMNS,
    persistence: persistedBlotterTable,
    rowID: 'TransactionID',
    density: BlotterDensity.Comfortable,
    clientLocalFilter,
    gridOptions: {
      animateRows: true,
      rowHeight: 40,
    },
  });

  return useMemo(
    () => ({ blotterTable, blotterTableFilterProps, filterBuilderAccordion }),
    [blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
};

function onlyServerFilterKeys(filter: DateRangeFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['EndDate', 'StartDate', 'MarketAccounts']);
}
